import React from 'react';
import { AppIcon } from '@components/AvAppTag';
import { HeadCellType, OptionQueryType, SortType } from '@components/Table/types';
import { flex } from '@fabric-ui/theme';
import { Box } from '@mui/material';
import { removeByField } from '@utils/filter/remove-filter-field';
import { isEmptyObject } from '@utils/filterUtils';
import { gql } from 'graphql-request';
import { Filter } from '../../types/filter.types';

export const getHeadCells = (filters: Filter): HeadCellType[] => {
  const asyncGetOptions = asyncOptionsGetter(filters);
  const headCells: HeadCellType[] = [
    {
      id: 'id',
      label: 'Account ID',
      isKey: true,
      asyncGetOptions,
    },
    {
      id: 'name',
      label: 'Name',
      asyncGetOptions,
      initSort: SortType.ASC,
    },
    {
      id: 'logo',
      label: 'Logo',
      formatter: src => <img height="38" src={`/${src || 'logos/placeholder.webp'}`} alt="logo" />,
      disableFilter: true,
      disableSortBy: true,
    },
    {
      id: 'accountType',
      label: 'Type',
      asyncGetOptions,
    },
    {
      id: 'apps',
      label: 'Apps',
      disableFilter: true,
      disableSortBy: true,
      formatter: apps => (
        <Box sx={{ ...flex.row, gap: 1.5 }}>
          {apps
            .filter(({ name }) => name)
            .map(({ name }) => (
              <AppIcon key={name} app={name} />
            ))}
        </Box>
      ),
    },
    {
      id: 'industry',
      label: 'Industry',
      asyncGetOptions,
    },
  ];

  return headCells.map(cell =>
    cell.asyncGetOptions
      ? {
          ...cell,
          asyncGetOptions: cell.asyncGetOptions.bind(cell),
        }
      : cell
  );
};

const GET_FILTER_VALUES = gql`
  query findAccountFieldValues($fieldName: String, $filterablePage: FilterablePage) {
    findAccountFieldValues(fieldName: $fieldName, filterablePage: $filterablePage) {
      content
      totalPages
      totalElements
      number
      last
    }
  }
`;

const asyncOptionsGetter = (nonFilteredFilter: Filter) =>
  function getOptions(this: HeadCellType, search: string | undefined) {
    const { id: fieldName } = this;
    const filter = isEmptyObject(nonFilteredFilter) ? {} : removeByField(nonFilteredFilter, fieldName) || {};

    return {
      type: OptionQueryType.GQL_PAGINATION,
      query: GET_FILTER_VALUES,
      queryKey: ['headCell', fieldName, search, JSON.stringify(filter)],
      variables: {
        fieldName,
        filterablePage: {
          filter: {
            and: {
              operands: [
                filter,
                search
                  ? {
                      expression: {
                        fieldName,
                        stringCondition: {
                          contains: search,
                        },
                      },
                    }
                  : {
                      // get only populated fields
                      expression: {
                        fieldName,
                        stringCondition: {
                          notEmpty: {},
                        },
                      },
                    },
              ],
            },
          },
          pageable: {
            pageNumber: 0,
            pageSize: 100,
            sortBy: fieldName,
            order: 'asc',
          },
        },
      },
      select: (data: any) => ({ data: data?.content?.map((value: string) => ({ title: value, value })), totals: data?.totalElements }),
    };
  };
